import Container from '@mui/material/Container';
import Grid from '@mui/material/Unstable_Grid2';
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import LoadingButton from '@mui/lab/LoadingButton';
import Paper from "@mui/material/Paper";
import Avatar from '@mui/material/Avatar';
import AvatarGroup from '@mui/material/AvatarGroup';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Chip from "@mui/material/Chip";
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

import {
  GridColDef,
} from '@mui/x-data-grid';

import { useState } from 'react';
import useModal from '../../hooks/use-modal';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { CustomDataGrid } from '../../components/common/datagrid';
import { ProjectDetailStyle } from '../../styles/pages/project/project-detail'
import { useQueryProjectDetail, useMutationSendCollaborationReq, useQueryDownloadCSV } from '../../api/project';
import { ProjectDetailById } from '../../types/project';
import { selectAuthUser } from '../../store/auth/selector';

type DescriptionContentProps = {
	descriptionContent: string | undefined
};

function FormattedDescription({ descriptionContent}: DescriptionContentProps) {
  const paragraphs = descriptionContent?.split('\n');

  return (
    <Box>
      {paragraphs?.map((paragraph, idx) => (
        <Typography key={`paragraph-${paragraph}`} variant="body1" sx={ProjectDetailStyle.contentDescription}>
          {paragraph}
        </Typography> 
      ))}
    </Box>
  );
}

const FormattedKeyword = (keyword: string | undefined) => {
  const result = keyword?.replace(/AND,|OR,/g, ' ');
  return result
}

export function ProjectDetail() {
  const { showModal } = useModal();
  const navigate = useNavigate()
  const { id } = useParams();
  const queryClient = useQueryClient();
  const profile = useSelector(selectAuthUser);
  
  const [totalItems, setTotalItems] = useState(0)
  const [paginationModel, setPaginationModel] = useState({pageSize: 10, page: 0});
  const [openRequestCollabModal, setOpenRequestCollabModal] = useState(false);
  const [reasonCollaborationReq, setReasonCollaborationReq] = useState('');
  const [projectDetail, setProjectDetail] = useState<ProjectDetailById | null>(null);
  const [loadDownloadCSV, setLoadDownloadCsv] = useState(false);
  const [doDownloadCsv, setDoDownloadCsv] = useState(false);
  
  const columns: GridColDef[] = [
    {
      field: 'title',
      headerName: 'Title',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'link',
      headerName: 'Link',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'date',
      headerName: 'Date',
      flex: 0.5,
      minWidth: 100,
    },
    {
      field: 'tags',
      headerName: 'Tags',
      flex: 0.5,
      minWidth: 100,
    },
    {
      field: 'content',
      headerName: 'Content',
      flex: 1,
      minWidth: 200,
    },
  ];

  const getProjectDetail = useQueryProjectDetail({
    query: {
      userId: profile.userId,
      projectId: `${id}`,
      take: paginationModel.pageSize,
      page: paginationModel.page
    },
    options: {
      // enabled: false,
      onSuccess: (res) => {
        setProjectDetail(res.data);
        setTotalItems(res.data.articles.totalItems);
      },
      onError: () => {
        setProjectDetail(null);
      }
    },
  }); 

  const requestCollaboration = useMutationSendCollaborationReq({
    onSettled: async (res) => {
      if (res) {
        queryClient.invalidateQueries({ queryKey: ['projectDetail'] });
      }
    },
    onSuccess: async (res) => {
      setOpenRequestCollabModal(false)
      showModal({
        title: 'Success',
        message:
          'Send collaboration request successfully.',
        options: {
          variant: 'success',
        },
      });
    },
    onError: (error) => {
      showModal({
        title: 'Error',
        message:
          'Send collaboration request is failed.',
        options: {
          variant: 'failed',
        },
      });
    },
  });

  const sendRequestCollaboration = async () => {
    requestCollaboration.mutate({
      ProjectId: `${id}`,
      UserId: profile.userId,
      Reason: reasonCollaborationReq,
    });
  }

  const downloadCSV = useQueryDownloadCSV({
    query: {
      projectId: `${id}`,
    },
    options: {
      enabled: doDownloadCsv,
      onSuccess: (res) => {
        setLoadDownloadCsv(false);

        const blob = new Blob([res], { type: 'text/csv' });
        const url = window.URL.createObjectURL(new Blob([res]));
        const link = document.createElement('a');
        link.href = url;
        link.download = `${projectDetail?.projectName}-${projectDetail?.startDate}-${projectDetail?.endDate}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
      },
      onError: () => {
        setLoadDownloadCsv(false);

        showModal({
          title: 'Error',
          message:
            'Download article is failed.',
          options: {
            variant: 'failed',
          },
        });
      }
    },
  });

  return (
    <Box sx={ProjectDetailStyle.contentBox}>
      <Container maxWidth={false}>
        <Grid container spacing={2}>
          <Grid xs={12}>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" onClick={() => navigate('/project')}>
                Project
              </Link>
              <Link
                underline="hover"
                color="text.primary"
                onClick={() => navigate('/project-detail')}
                aria-current="page"
              >
                Project Detail
              </Link>
            </Breadcrumbs>
          </Grid>
          <Grid xs={12}>
            <Box sx={ProjectDetailStyle.contentBoxContainer1GridItemBox}>
              <Typography variant="h3" sx={ProjectDetailStyle.contentBoxContainer1GridItemBoxTypography}>Project Detail</Typography>
              {projectDetail?.requestStatus === "Approved"  && projectDetail.access === true && (
                <Chip label="Approved" sx={ProjectDetailStyle.contentBoxContainer1GridItemBoxTypographyChip1}/>
              )}
              {projectDetail?.requestStatus === "Rejected" && projectDetail.access === false && (
                <Chip label="Rejected" sx={ProjectDetailStyle.contentBoxContainer1GridItemBoxTypographyChip2}/>
              )}
              {projectDetail?.requestStatus === "Waiting" && projectDetail.access === false && (
                <Chip label="Waiting for Approval" sx={ProjectDetailStyle.contentBoxContainer1GridItemBoxTypographyChip3}/>
              )}
              {projectDetail?.requestStatus === null && projectDetail.access === false  && (
                <Button variant="contained" color="success" startIcon={<PersonAddAltIcon />} onClick={() => {setOpenRequestCollabModal(true)}}>Send Request for Collaboration</Button>
              )} 
            </Box>
          </Grid>
        </Grid>
      </Container>
      <Container maxWidth="md">
        <Paper elevation={0} sx={ProjectDetailStyle.contentBoxContainer2Paper}>
          <Grid container spacing={2}>
            <Grid xs={12}>
              <Typography variant="h3" sx={ProjectDetailStyle.contentBoxContainer2PaperGridItem1Typography}>{projectDetail?.projectName}</Typography>
            </Grid>
            <Grid xs={12}>
              <Box sx={ProjectDetailStyle.contentBoxContainer2PaperGridItem2Box}>
                <Typography>{projectDetail?.projectOwner}</Typography>
                <Typography color="textSecondary">{projectDetail?.publishedDate}</Typography>
              </Box>
            </Grid>
            <Grid xs={12}>
              <Box sx={ProjectDetailStyle.contentBoxContainer2PaperGridItem3Box}>
                {projectDetail && projectDetail.collaborators?.length > 0 ? (
                  <AvatarGroup spacing="small" max={3} sx={ProjectDetailStyle.contentBoxContainer2PaperGridItem3BoxAvatarGroup}>
                  {projectDetail.collaborators.map((collab) => (
                    <Avatar key={collab.userId} alt={collab.fullName} src={collab.avatar}/>
                  ))}
                </AvatarGroup>
                ) : ""}
                <Typography variant="body1">{projectDetail?.totalCollaborators} Collaborators</Typography>
              </Box>
            </Grid>
            <Grid xs={12}>
              <Box
                component="img"
                sx={ProjectDetailStyle.contentBoxContainer2PaperGridItem4}
                alt="article-banner"
                src={projectDetail?.projectThumbnail}
              />
            </Grid>
            <Grid xs={12}>
              <Typography variant="h4" sx={ProjectDetailStyle.contentBoxContainer2PaperGridItem5Typography}>Description</Typography>
              <FormattedDescription descriptionContent={projectDetail?.projectDescription}/>
            </Grid>
            <Grid xs={12}>
              <Typography variant="h4" sx={ProjectDetailStyle.contentBoxContainer2PaperGridItem6Typography}>Keyword</Typography>
              <Typography variant="body1">{FormattedKeyword(projectDetail?.keywords)}</Typography>
            </Grid>
            <Grid xs={12}>
              <Typography variant="h4" sx={ProjectDetailStyle.contentBoxContainer2PaperGridItem7Typography}>Project Period</Typography>
              <Typography variant="body1">{projectDetail?.startDate} - {projectDetail?.endDate}</Typography>
            </Grid>
          </Grid>
        </Paper>
      </Container>
      <Container maxWidth="lg">  
        <Grid container spacing={2}>
          <Grid xs={12}>
            <Box sx={ProjectDetailStyle.contentBoxContainer3GridItem}>
              <Typography variant="h4" sx={ProjectDetailStyle.contentBoxContainer2PaperGridItem7Typography}>Sample Data</Typography>
              {(profile?.role === "Admin" || profile.role === "Lecturer" || (profile.role === "Student" && projectDetail?.access === true)) && (
                <LoadingButton
                  variant="contained"
                  startIcon={<FileDownloadOutlinedIcon />}
                  loading={loadDownloadCSV}
                  loadingPosition="start"
                  disabled={projectDetail?.articles.articles?.length === 0 && true}
                  onClick={() => {
                    setLoadDownloadCsv(true)
                    setDoDownloadCsv(true)
                  }}
                >
                  <span>Download CSV</span>
                </LoadingButton>
              )}
            </Box>
          </Grid>
          <Grid xs={12}>
            <Paper elevation={3}>
              <CustomDataGrid
                columnsData={columns}
                rowsData={projectDetail ? projectDetail.articles.articles : []}
                rowCount={totalItems}
                getRowId={(row) => row.articleId}
                getRowHeight={() => 'auto'}
                setPaginationModel={(value) => {
                  setPaginationModel(value);
                }} 
                paginationModel={paginationModel}
              />
            </Paper>
          </Grid>
        </Grid>
      </Container>
      <Dialog
      open={openRequestCollabModal}
      onClose={() => {setOpenRequestCollabModal(true)}}
      // scroll="paper"
      fullWidth
      maxWidth="sm"
      aria-labelledby="dialog-title"
      aria-describedby="dialog-description"
    >
      <DialogTitle id="scroll-dialog-title" sx={ProjectDetailStyle.dialogDialogTitle}>Collaboration Request</DialogTitle>
      <DialogContent>
        <Box component="form" autoComplete="off" noValidate mt={0.5}>
          <TextField
            fullWidth
            id="outlined-multiline-static"
            label="Add reason or comment for collaboration"
            multiline
            rows={5}
            onChange={(e) => setReasonCollaborationReq(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="error" onClick={() => {setOpenRequestCollabModal(false)}}>Cancel</Button>
        <Button variant="contained" color="success" onClick={sendRequestCollaboration}>Send Request</Button>
      </DialogActions>
      </Dialog>
    </Box>
  );
}